import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseModel } from 'model/BaseModel';
import Pageable from 'model/page/Pageable';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { confirmDialog } from 'primereact/confirmdialog';
import { DataTable, DataTableFilterMeta, DataTableRowData, DataTableRowExpansionTemplate, DataTableStateEvent, DataTableValueArray } from 'primereact/datatable';
import React, { ReactNode, useEffect, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router';
import { BaseServiceCadastros } from 'service/BaseServiceCadastros';
import Base from './Base';
import { useToastContext } from './alert/ToastContext';
import { useSearchParams } from 'react-router-dom';
import { BaseEditModel } from 'model/BaseEditModel';
import { useFilter } from 'contexts/FilterContext';

type Props = {
    children?: ReactNode;
    ordenacao: string;
    service: Pick<BaseServiceCadastros<BaseModel|BaseEditModel>, 'pesquisar'>
        & Partial<Pick<BaseServiceCadastros<BaseModel|BaseEditModel>, 'excluir'>>;
    urlService: string;
    tituloPagina: string;
    showNewButton?: boolean;
    showEditButton?: boolean;
    showRemoveButton?: boolean;
    buttonsExtra?: Array<ReactNode>;
    defaultFilters?: DataTableFilterMeta;
    showClearFilterButton?: boolean;
    onChangeFilters?: (filters: DataTableFilterMeta) => void;
    nome?: string;
    footerGroup?: (totalizadores: any) => ReactNode;
    rowExpansionTemplate?: (data: BaseModel, options: DataTableRowExpansionTemplate) => ReactNode;
};
type BaseListagemRef ={
    reload: () => void;
}
/**
 * Garante que os filtros em cache (FilterContext) tenham exatamente as chaves do defaultFilters.
 * Sem isso, quando o shape do filtro de uma tela muda (chave renomeada/removida), o estado antigo
 * persistido em memória (preservado pelo Fast Refresh ou navegação) deixa o PrimeReact acessar
 * filters[field] indefinido, quebrando a renderização e o "Limpar" da coluna.
 */
function reconciliarFiltros(cached: DataTableFilterMeta | null, defaults?: DataTableFilterMeta): DataTableFilterMeta {
    if (!defaults) {
        return cached || {};
    }
    if (!cached) {
        return defaults;
    }
    const resultado: DataTableFilterMeta = {};
    Object.keys(defaults).forEach((key) => {
        resultado[key] = cached[key] !== undefined ? cached[key] : defaults[key];
    });
    return resultado;
}

const DefaultBaseListagem = (props: Props, ref: React.Ref<BaseListagemRef>) => {

    const [queryParameters] = useSearchParams();
    const filterContext = useFilter();

    const { msgSucesso, msgErro, msgAlerta } = useToastContext();

    const navigate = useNavigate();

    const [filtrando, setFiltrando] = useState<boolean>(false);
    const [rows, setRows] = useState<BaseModel[]>([]);
    const [pageable, setPageable] = useState(props.nome && filterContext.pageables[props.nome]? filterContext.pageables[props.nome] : new Pageable(0, 10, props.ordenacao, 0));
    const [totalElements, setTotalElements] = useState<number>(0);
    const [excluindo, setExcluindo] = useState<boolean>(false);
    const [idConta] = useState<number | null>(queryParameters.has('idConta') ? parseInt(queryParameters.get('idConta') || '0') : null);
    const [filters, setFilters] = useState<DataTableFilterMeta>(reconciliarFiltros(props.nome && filterContext.filters[props.nome] ? filterContext.filters[props.nome] : null, props.defaultFilters));
    const [totalizadores, setTotalizadores] = useState<any>(null);
    const [expandedRows, setExpandedRows] = useState<any>([]);

    useEffect(() => {
        if(props.nome && filterContext.filters[props.nome] && props.onChangeFilters){
            props.onChangeFilters(filterContext.filters[props.nome])
        }
        setFiltrando(true);
    }, []);

    useEffect(() => {
        if (filtrando) {
            if(props.nome){
                filterContext.updateFilters(props.nome, filters);
                filterContext.updatePageable(props.nome, pageable);
            }
            
            props.service
                .pesquisar(pageable, filters, idConta)
                .then(lista => {
                    if(lista.totalizadores){

                        setTotalizadores(lista.totalizadores);
                    }
                    setFiltrando(false);
                    setRows(lista.content);
                    setTotalElements(lista.totalElements);
                })
                .catch(err => {
                    setFiltrando(false);
                });
        }
    }, [filtrando, pageable]);

    useEffect(() => {
        setFiltrando(true);
    }, [filters, pageable]);

    useImperativeHandle(ref, () => ({
        reload() {
            setFiltrando(true);
        }
    }));

    const onPage = (event: DataTableStateEvent) => {

        setPageable({ ...pageable, page: event.page !== undefined ? event.page : 0, first: event.first });
        setFiltrando(true);

    };

    function novo() {
        let url = `${props.urlService}/cadastrar`;
        if (idConta) {
            url += `?idConta=${idConta}`;
        }
        navigate(`${url}`);
    }

    function editar(item: BaseModel) {
        let identificador = item.id? item.id: item.codigo;
        let url = `${props.urlService}/editar/${identificador}`;
        if (idConta) {
            url += `?idConta=${idConta}`;
        }
        navigate(`${url}`);
    }

    function remover(item: BaseModel) {

        confirmDialog({
            acceptLabel: 'Sim',
            rejectLabel: 'Não',
            message: 'Deseja realmente excluir este registro?',
            header: 'Confirmação',
            icon: 'pi pi-exclamation-triangle',
            accept: () => removerConfirm(item),
        });


    }

    function removerConfirm(item: BaseModel) {

        if (!props.service.excluir) return;

        setExcluindo(true);
        let identificador = item.id? item.id: item.codigo;
        if (identificador) {

            props.service
                .excluir(identificador, idConta)
                .then(() => {
                    setExcluindo(false);
                    setFiltrando(true);
                    msgSucesso(props.tituloPagina, 'Registro removido com sucesso!');
                })
                .catch(error => {
                    setExcluindo(false);
                    console.error(error);
                    if (error && error.data && error.data.message) {
                        msgErro(props.tituloPagina, error.data.message);
                    } else {
                        msgErro(props.tituloPagina, 'Ocorreu um erro ao tentar remover o registro, tente novamente!');
                    }

                });
        }

    }

    function getHeader() {

        return (
            <div style={{display: "flex", justifyContent: "space-between", textAlign: 'center', alignContent: "center"}}>
                <div></div>
                <span style={{ textAlign: 'center', alignSelf: "center",  position: 'relative' }}>{props.tituloPagina}</span>
                <div >
                    {props.showClearFilterButton && 
                        <Button type="button" icon="pi pi-filter-slash" label="Limpar" outlined onClick={clearFilter} style={{marginRight:8}} />
                    }
                    {props.buttonsExtra && props.buttonsExtra.map(button => {
                        return button;
                    })}
                    {props.showNewButton &&
                        <Button
                            style={{  }}
                            type="button" icon={<FontAwesomeIcon icon="add" />}
                            className="ui-button-info"
                            onClick={() => novo()}
                        ></Button>
                    }
                </div>


            </div>
        )
    }
    
    const clearFilter = () => {
        setFilters(props.defaultFilters || {});
    }

    return (
        <Base tituloPagina={props.tituloPagina}>
            <div className="card" style={{ marginTop: 8 }}>
                <DataTable
                    emptyMessage="Nenhum resultado encontrado"
                    onSort={(e: DataTableStateEvent) => {
                        let sort = e.sortField ? e.sortField : "";
                        sort += e.sortOrder === 1 ? ",ASC" : e.sortOrder === -1 ? ",DESC" : "";
                        setPageable({ ...pageable, sort: sort });

                        console.log(e);
                        
                    }}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="{first} - {last} de {totalRecords}"
                    sortField={pageable.sort.split(",")[0]}
                    sortOrder={pageable.sort.split(",")[1] === "ASC" ? 1 : pageable.sort.split(",")[1] === "DESC" ? -1 : 0}
                    filters={filters}
                    footerColumnGroup={props.footerGroup ? props.footerGroup(totalizadores) : null}
                    onFilter={(e) => {
                        setFilters(e.filters)
                        if(props.onChangeFilters){
                            props.onChangeFilters(e.filters);
                        }
                    }}
                    header={getHeader()}
                    value={rows} lazy dataKey="id" paginator
                    first={pageable.first} rows={10} totalRecords={totalElements} onPage={onPage}
                    loading={filtrando} tableStyle={{ minWidth: '75rem' }}
                    expandedRows={props.rowExpansionTemplate ? expandedRows : undefined} 
                    rowExpansionTemplate={props.rowExpansionTemplate}
                    onRowToggle={props.rowExpansionTemplate ? (e) => setExpandedRows(e.data) : undefined}
                >
                    {props.children}
                    {(props.showEditButton || props.showRemoveButton) && <Column key="colOpcoes" style={ props.showEditButton && props.showRemoveButton ? { whiteSpace: 'nowrap', width: '7em' } : { whiteSpace: 'nowrap', width: '3em' }}
                        body={(item) => <div>
                            {props.showEditButton && <Button type="button" icon={<FontAwesomeIcon icon="edit" />} className="ui-button-info" onClick={() => editar(item)}></Button>}
                            {props.showRemoveButton && <Button type="button" icon={<FontAwesomeIcon icon="remove" />} className="ui-button-danger" style={{ marginLeft: 8 }} onClick={() => remover(item)}></Button>}
                        </div>}
                    />}
                </DataTable>
            </div>
        </Base>
    );
};


const BaseListagem = React.forwardRef(DefaultBaseListagem);

BaseListagem.defaultProps = {
    showNewButton: true,
    showEditButton: true,
    showRemoveButton: true,
    buttonsExtra: [],
    showClearFilterButton: false
};

export type { BaseListagemRef as BaseListagemRef };
export default BaseListagem;
